import { useVirtualizer } from '@tanstack/react-virtual';
import * as React from 'react';
import { Vegobjekt } from '@/domain/vegobjekter/Vegobjekt';
import { vegSysRefComparator } from '@/domain/omrader';

interface Props {
    vegobjekter: Vegobjekt[];
    onObjectClick: (vegobjekt: Vegobjekt) => void;
}

const ROW_HEIGHT = 24;

export const VegSysRefList = ({
    vegobjekter: unsortedVegobjekter,
    onObjectClick,
}: Props) => {
    const scrollElementRef = React.useRef<HTMLUListElement>(null);
    const vegobjekter = React.useMemo(() => {
        const sortedVegobjekter = unsortedVegobjekter.map(vegobjekt => ({
            ...vegobjekt,
            lokasjon: {
                ...vegobjekt.lokasjon,
                vegsystemreferanser: [...vegobjekt.lokasjon.vegsystemreferanser].sort(
                    vegSysRefComparator
                ),
            },
        }));

        sortedVegobjekter.sort((a: Vegobjekt, b: Vegobjekt) => {
            return vegSysRefComparator(
                a.lokasjon.vegsystemreferanser[0],
                b.lokasjon.vegsystemreferanser[0]
            );
        });

        return sortedVegobjekter;
    }, [unsortedVegobjekter]);
    const virtualizer = useVirtualizer({
        count: vegobjekter.length,
        getScrollElement: () => scrollElementRef.current,
        estimateSize: () => ROW_HEIGHT,
        overscan: 5,
    });

    return (
        <ul ref={scrollElementRef} className="c-roadref-list">
            <li
                aria-hidden="true"
                className="c-roadref-list__spacer"
                style={{ height: virtualizer.getTotalSize() }}
            />
            {virtualizer.getVirtualItems().map(virtualRow => {
                const vegobjekt = vegobjekter[virtualRow.index];
                return (
                    <li
                        key={vegobjekt.id}
                        data-index={virtualRow.index}
                        className="c-roadref-list__element"
                        style={{ transform: `translateY(${virtualRow.start}px)` }}
                    >
                        <button
                            type="button"
                            className="c-roadref-list__button"
                            aria-label={`Vis vegobjekt med id: ${vegobjekt.id}`}
                            id={'vegobjekt_id_' + vegobjekt.id.toString()}
                            onClick={() => onObjectClick(vegobjekt)}
                        >
                            <span className="c-roadref-list__button-vegobjekt">
                                {vegobjekt.id}
                            </span>
                            <span className="c-roadref-list__button-vsr">
                                {getSecondSpanContent(vegobjekt)}
                            </span>
                        </button>
                    </li>
                );
            })}
        </ul>
    );
};

function getSecondSpanContent(vegobjekt: Vegobjekt): string {
    if (vegobjekt.metadata?.type?.id === 580) {
        const navn = vegobjekt.egenskaper.find(e => e.navn === 'Navn')?.verdi;
        return navn ? String(navn) : '';
    }

    if (vegobjekt.metadata?.type?.id === 704) {
        const riksvegrute = vegobjekt.egenskaper.find(
            e => e.navn === 'Riksvegrute'
        )?.verdi;
        return riksvegrute ? String(riksvegrute) : '';
    }

    const vegSysRefs = vegobjekt.lokasjon.vegsystemreferanser;
    const lengthInfo = ' (' + vegSysRefs.length + ')';

    switch (vegSysRefs.length) {
        case 0:
            return 'Ingen vegsystemreferanser';
        case 1:
            return ' ' + vegSysRefs[0].toVegkartKortform();
        default:
            return (
                ' ' +
                [
                    ...new Set(
                        vegSysRefs.map(vsr => {
                            const vegsystem = vsr.vegsystem;
                            if (!vegsystem) {
                                return vsr.toVegkartKortform();
                            }

                            return vegsystem.vegkategori
                                .concat(vegsystem.fase || '')
                                .concat(vegsystem.nummer + '')
                                .concat(
                                    vsr.strekning
                                        ? ' S' +
                                              vsr.strekning.strekningsNummer.toString() +
                                              'D' +
                                              vsr.strekning.delstrekningsNummer?.toString()
                                        : ''
                                );
                        })
                    ),
                ].join(', ') +
                lengthInfo
            );
    }
}
